/**
 * useRoleAccess Hook
 *
 * Rol tabanlı erişim kontrolü sağlayan merkezi hook.
 * Menüler, slider'lar ve homepage bileşenlerinin görünürlüğünü yönetir.
 *
 * @module hooks/useRoleAccess
 */

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Rol tabanlı erişim kontrolü hook'u
 * @returns {Object} Rol erişim fonksiyonları ve kullanıcı bilgileri
 */
const useRoleAccess = () => {
	// Redux store'dan kullanıcı ve bileşen bilgilerini al
	const authUser = useSelector((store) => store.auth.authUser);
	const activeComponents = useSelector((store) => store.auth.activeComponents);

	/**
	 * Kullanıcının mevcut rolü
	 * @type {string|null}
	 */
	const userRole = useMemo(() => {
		return authUser?.roleName || null;
	}, [authUser]);

	/**
	 * Kullanıcının rol ID'si
	 * @type {number|null}
	 */
	const roleId = useMemo(() => {
		return authUser?.roleId || null;
	}, [authUser]);

	/**
	 * Belirli bir bileşenin aktif olup olmadığını kontrol eder
	 * @param {string} componentName - Kontrol edilecek bileşen adı
	 * @returns {boolean} Bileşen aktif ise true
	 */
	const isComponentActive = useCallback(
		(componentName) => {
			// ComponentName belirtilmemişse her zaman aktif kabul et
			if (!componentName) return true;

			// ActiveComponents listesi yoksa false döndür
			if (!activeComponents || !activeComponents.length) return false;

			// Aktif bileşen listesinde ara
			return activeComponents.some(
				(item) => item.active && item.componentName === componentName
			);
		},
		[activeComponents]
	);

	/**
	 * Belirli bir kategori altındaki herhangi bir bileşenin aktif olup olmadığını kontrol eder
	 * @param {string} componentCategory - Kontrol edilecek kategori adı
	 * @returns {boolean} Kategoride aktif bileşen varsa true
	 */
	const isCategoryActive = useCallback(
		(componentCategory) => {
			if (!componentCategory) return true;
			if (!activeComponents || !activeComponents.length) return false;

			return activeComponents.some(
				(item) => item.active && item.componentCategory === componentCategory
			);
		},
		[activeComponents]
	);

	/**
	 * Belirli bir rolün bileşene erişimi olup olmadığını kontrol eder
	 * @param {string} componentName - Bileşen adı
	 * @param {string|string[]} allowedRoles - İzin verilen roller
	 * @returns {boolean} Erişim izni varsa true
	 */
	const hasRoleAccess = useCallback(
		(componentName, allowedRoles = []) => {
			// Önce bileşenin aktif olup olmadığını kontrol et
			if (!isComponentActive(componentName)) return false;

			// Eğer allowedRoles boşsa, sadece component aktivasyonuna bak
			if (!allowedRoles || allowedRoles.length === 0) return true;

			// Kullanıcı rolü yoksa erişim yok
			if (!userRole) return false;

			// Rolleri array'e dönüştür
			const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

			// Kullanıcının rolü izin verilen roller arasında mı?
			return roles.includes(userRole);
		},
		[isComponentActive, userRole]
	);

	/**
	 * Slider/Duyuru için rol kontrolü yapar
	 * @param {Object} item - Slider/Duyuru objesi
	 * @param {string} [item.role] - Slider'ın hedef rolü
	 * @returns {boolean} Slider gösterilmeli ise true
	 */
	const canViewSlider = useCallback(
		(item) => {
			// Item yoksa gösterme
			if (!item) return false;

			// Rol belirtilmemişse herkese göster
			if (!item.role) return true;

			// Kullanıcı rolü yoksa gösterme
			if (!userRole) return false;

			// Rol eşleşmesi kontrolü
			return item.role === userRole;
		},
		[userRole]
	);

	/**
	 * Duyuru listesini rol bazlı filtreler
	 * @param {Array} announcements - Duyuru listesi
	 * @returns {Array} Filtrelenmiş duyuru listesi
	 */
	const filterAnnouncementsByRole = useCallback(
		(announcements) => {
			if (!announcements || !Array.isArray(announcements)) return [];

			return announcements.filter((item) => canViewSlider(item));
		},
		[canViewSlider]
	);

	/**
	 * Menü öğelerini rol bazlı filtreler
	 * @param {Array} menuItems - Menü öğeleri listesi
	 * @returns {Array} Filtrelenmiş menü listesi
	 */
	const filterMenusByRole = useCallback(
		(menuItems) => {
			if (!menuItems || !Array.isArray(menuItems)) return [];

			return menuItems
				.map((menuItem) => {
					// Ana menü öğesi kontrolü
					if (!isComponentActive(menuItem.componentName)) {
						return null;
					}

					// Alt menü öğeleri kontrolü (varsa)
					if (menuItem.subMenu && Array.isArray(menuItem.subMenu)) {
						const filteredSubMenu = menuItem.subMenu.filter((subItem) =>
							isComponentActive(subItem.componentName)
						);

						// Eğer alt menü öğeleri kalmadıysa, ana menüyü de gösterme
						if (filteredSubMenu.length === 0) {
							return null;
						}

						return {
							...menuItem,
							subMenu: filteredSubMenu,
						};
					}

					return menuItem;
				})
				.filter((item) => item !== null);
		},
		[isComponentActive]
	);

	/**
	 * Kullanıcının giriş yapıp yapmadığını kontrol eder
	 * @type {boolean}
	 */
	const isAuthenticated = useMemo(() => {
		return !!authUser;
	}, [authUser]);

	return {
		// Kullanıcı bilgileri
		userRole,
		roleId,
		isAuthenticated,
		authUser,
		activeComponents,

		// Kontrol fonksiyonları
		isComponentActive,
		isCategoryActive,
		hasRoleAccess,
		canViewSlider,

		// Filtreleme fonksiyonları
		filterAnnouncementsByRole,
		filterMenusByRole,
	};
};

export default useRoleAccess;
